'use client';

import { useState } from 'react';
import { motion } from 'motion/react';

import { SmartIcon } from '@/shared/blocks/common';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/shared/components/ui/accordion';
import { ScrollAnimation } from '@/shared/components/ui/scroll-animation';
import { Section } from '@/shared/types/blocks/landing';

export function FeaturesAccordion({
  section,
  className,
}: {
  section: Section;
  className?: string;
}) {
  const [activeItem, setActiveItem] = useState<string>('item-1');

  return (
    <section
      className={`overflow-x-hidden py-16 md:py-24 ${section.className} ${className}`}
    >
      <div className="container space-y-8 overflow-x-hidden px-2 sm:px-6 md:space-y-16 lg:space-y-20">
        <ScrollAnimation>
          <div className="mx-auto max-w-4xl text-center text-balance">
            <h2 className="text-foreground mb-4 text-3xl font-semibold tracking-tight md:text-4xl">
              {section.title}
            </h2>
            <p className="text-muted-foreground mb-6 md:mb-12 lg:mb-16">
              {section.description}
            </p>
          </div>
        </ScrollAnimation>

        {/* Redesigned layout - Full width accordion */}
        <ScrollAnimation delay={0.1}>
          <div className="mx-auto max-w-3xl">
            <div className="space-y-3">
              <Accordion
                type="single"
                value={activeItem}
                onValueChange={(value) => setActiveItem(value as string)}
                className="w-full"
              >
                {section.items?.map((item, idx) => (
                  <AccordionItem
                    value={`item-${idx + 1}`}
                    key={idx}
                    className="border border-foreground/10 rounded-lg px-4 md:px-6 py-3 md:py-4 !border-b !border-foreground/10"
                  >
                    <AccordionTrigger className="hover:text-foreground/80 text-left py-0">
                      <div className="flex items-center gap-3 md:gap-4 text-left">
                        {item.icon && (
                          <motion.div
                            initial={{ scale: 1 }}
                            animate={{ scale: activeItem === `item-${idx + 1}` ? 1.1 : 1 }}
                            transition={{ duration: 0.2 }}
                            className="flex-shrink-0"
                          >
                            <div className="p-2 rounded-lg bg-gradient-to-br from-primary/20 to-primary/5 dark:from-primary/30 dark:to-primary/10">
                              <SmartIcon name={item.icon as string} size={20} className="text-primary" />
                            </div>
                          </motion.div>
                        )}
                        <span className="font-semibold text-base md:text-lg text-foreground">
                          {item.title}
                        </span>
                      </div>
                    </AccordionTrigger>

                    <AccordionContent className="px-0 pb-0 pt-4 text-muted-foreground leading-relaxed">
                      <motion.div
                        initial={{ opacity: 0, y: -8 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -8 }}
                        transition={{ duration: 0.2 }}
                      >
                        {item.description}
                      </motion.div>
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </div>
        </ScrollAnimation>
      </div>
    </section>
  );
}
